import {
  BuiltInTool,
  BuiltInToolProvider,
  OpenAIWebSearchToolParam,
  GoogleWebSearchToolParam,
  GoogleMapsToolParam,
  GoogleCodeExecutionToolParam,
  GoogleUrlContextToolParam,
  GoogleFileSearchToolParam,
  AnthropicBashToolParam,
  AnthropicWebSearchToolParam,
  AnthropicCodeExecutionToolParam,
  AnthropicTextEditorToolParam,
  FileSearchToolParam,
  CodeInterpreterToolParam,
  ImageGenerationToolParam,
  OpenAINativeMcpToolParam,
  ShellToolParam,
  ApplyPatchToolParam,
  OpenRouterWebSearchToolParam,
  OpenRouterServerToolParam,
} from "@/types";
import type { BuiltInToolType } from "@/types/prompt-blueprint";

export interface GetBuiltInToolsOptions {
  modelName?: string | null;
  baseModel?: string | null;
  apiType?: string | null;
}

const VERTEX_MODEL_FAMILY_TO_TOOL_PROVIDER: [string, BuiltInToolProvider][] = [
  ["claude", "anthropic"],
  ["gemini", "google"],
];

const VERTEX_DEFAULT_TOOL_PROVIDER: BuiltInToolProvider = "google";

export const getVertexToolProvider = (
  modelName?: string | null,
  baseModel?: string | null,
): BuiltInToolProvider => {
  const name = (modelName ?? "").toLowerCase();
  const base = (baseModel ?? "").toLowerCase();
  for (const [substring, provider] of VERTEX_MODEL_FAMILY_TO_TOOL_PROVIDER) {
    if (name.includes(substring) || base.includes(substring)) {
      return provider;
    }
  }
  return VERTEX_DEFAULT_TOOL_PROVIDER;
};

const OPENAI_BUILT_IN_TOOLS: BuiltInTool[] = [
  {
    id: "openai_web_search",
    name: "Web Search",
    description: "This tool allows the model to search the web in real time.",
    provider: "openai",
    type: "web_search",
    config: {
      type: "web_search",
    } as OpenAIWebSearchToolParam,
  },
  {
    id: "openai_file_search",
    name: "File Search",
    description: "This tool allows the model to search through uploaded files.",
    provider: "openai",
    type: "file_search",
    config: {
      type: "file_search",
      vector_store_ids: ["<vector_store_id>"],
      max_num_results: 10,
    } as FileSearchToolParam,
  },
  {
    id: "openai_code_interpreter",
    name: "Code Interpreter",
    description:
      "This tool allows the model to write and execute Python code in a sandboxed environment.",
    provider: "openai",
    type: "code_interpreter",
    config: {
      type: "code_interpreter",
      container: { type: "auto" },
    } as CodeInterpreterToolParam,
  },
  {
    id: "openai_image_generation",
    name: "Image Generation",
    description: "This tool allows the model to generate or edit images using a text prompt.",
    provider: "openai",
    type: "image_generation",
    config: {
      type: "image_generation",
    } as ImageGenerationToolParam,
  },
  {
    id: "openai_mcp",
    name: "MCP (OpenAI Native)",
    description:
      "Let the OpenAI Responses API connect to and execute tools from a remote MCP server.",
    provider: "openai",
    type: "openai_mcp",
    config: {
      type: "mcp",
      server_label: "my_server",
      server_url: "https://example.com/mcp",
      require_approval: "never",
    } as OpenAINativeMcpToolParam,
  },
  {
    id: "openai_shell",
    name: "Shell",
    description: "This tool allows the model to execute shell commands in a managed environment.",
    provider: "openai",
    type: "shell",
    config: {
      type: "shell",
      environment: { type: "container_auto" },
    } as ShellToolParam,
  },
  {
    id: "openai_apply_patch",
    name: "Apply Patch",
    description:
      "This tool allows the model to propose structured diffs to create, update, or delete files.",
    provider: "openai",
    type: "apply_patch",
    config: {
      type: "apply_patch",
    } as ApplyPatchToolParam,
  },
];

const OPENROUTER_BUILT_IN_TOOLS: BuiltInTool[] = [
  {
    id: "openrouter_web_search",
    name: "Web Search",
    description:
      "Let any OpenRouter model search the web for current information (openrouter:web_search).",
    provider: "openrouter",
    type: "web_search",
    config: {
      id: "web",
    } as OpenRouterWebSearchToolParam,
  },
  {
    id: "openrouter_datetime",
    name: "Datetime",
    description: "Give the model the current date and time (openrouter:datetime).",
    provider: "openrouter",
    type: "datetime",
    config: {
      openrouter_server_tool: "datetime",
    } as OpenRouterServerToolParam,
  },
  {
    id: "openrouter_image_generation",
    name: "Image Generation",
    description: "Let the model generate images from text prompts (openrouter:image_generation).",
    provider: "openrouter",
    type: "image_generation",
    config: {
      openrouter_server_tool: "image_generation",
    } as OpenRouterServerToolParam,
  },
  {
    id: "openrouter_web_fetch",
    name: "Web Fetch",
    description: "Let the model fetch and extract content from URLs (openrouter:web_fetch).",
    provider: "openrouter",
    type: "web_fetch",
    config: {
      openrouter_server_tool: "web_fetch",
    } as OpenRouterServerToolParam,
  },
  {
    id: "openrouter_fusion",
    name: "Fusion",
    description: "Run a panel of models plus a judge for multi-model analysis (openrouter:fusion).",
    provider: "openrouter",
    type: "fusion",
    config: {
      openrouter_server_tool: "fusion",
    } as OpenRouterServerToolParam,
  },
  {
    id: "openrouter_advisor",
    name: "Advisor",
    description: "Consult a stronger model for guidance mid-generation (openrouter:advisor).",
    provider: "openrouter",
    type: "advisor",
    config: {
      openrouter_server_tool: "advisor",
    } as OpenRouterServerToolParam,
  },
  {
    id: "openrouter_subagent",
    name: "Subagent",
    description:
      "Delegate self-contained tasks to a smaller, faster worker model (openrouter:subagent).",
    provider: "openrouter",
    type: "subagent",
    config: {
      openrouter_server_tool: "subagent",
    } as OpenRouterServerToolParam,
  },
  {
    id: "openrouter_search_models",
    name: "Search Models",
    description:
      "Let the model search and filter the OpenRouter model catalog (openrouter:experimental__search_models).",
    provider: "openrouter",
    type: "search_models",
    config: {
      openrouter_server_tool: "experimental__search_models",
    } as OpenRouterServerToolParam,
  },
];

export const BUILT_IN_TOOLS: Record<BuiltInToolProvider, BuiltInTool[]> = {
  openai: OPENAI_BUILT_IN_TOOLS,
  "openai.azure": OPENAI_BUILT_IN_TOOLS,
  openrouter: OPENROUTER_BUILT_IN_TOOLS,
  google: [
    {
      id: "google_web_search",
      name: "Web Search",
      description:
        "This tool allows the model to search the web in real time (Grounding with Google Search).",
      provider: "google",
      type: "web_search",
      config: { google_search: {} } satisfies GoogleWebSearchToolParam,
    },
    {
      id: "google_maps",
      name: "Google Maps",
      description: "Ground responses with Google Maps place data (reviews, addresses, hours).",
      provider: "google",
      type: "google_maps",
      config: { google_maps: {} } satisfies GoogleMapsToolParam,
    },
    {
      id: "google_code_execution",
      name: "Code Execution",
      description: "Execute Python code in a sandboxed environment.",
      provider: "google",
      type: "code_interpreter",
      config: { code_execution: {} } satisfies GoogleCodeExecutionToolParam,
    },
    {
      id: "google_url_context",
      name: "URL Context",
      description: "Retrieve and analyze content from URLs provided in the prompt.",
      provider: "google",
      type: "url_context",
      config: { url_context: {} } satisfies GoogleUrlContextToolParam,
    },
    {
      id: "google_file_search",
      name: "File Search",
      description: "Search through uploaded files using semantic retrieval.",
      provider: "google",
      type: "file_search",
      config: {
        file_search: { file_search_store_names: ["<store_name>"] },
      } satisfies GoogleFileSearchToolParam,
    },
  ],
  vertexai: [
    {
      id: "vertexai_web_search_google",
      name: "Web Search",
      description:
        "This tool allows the model to search the web in real time (Grounding with Google Search).",
      provider: "vertexai",
      type: "web_search",
      config: { google_search: {} } satisfies GoogleWebSearchToolParam,
    },
    {
      id: "vertexai_maps_google",
      name: "Google Maps",
      description: "Ground responses with Google Maps place data (reviews, addresses, hours).",
      provider: "vertexai",
      type: "google_maps",
      config: { google_maps: {} } satisfies GoogleMapsToolParam,
    },
    {
      id: "vertexai_code_execution_google",
      name: "Code Execution",
      description: "Execute Python code in a sandboxed environment.",
      provider: "vertexai",
      type: "code_interpreter",
      config: { code_execution: {} } satisfies GoogleCodeExecutionToolParam,
    },
    {
      id: "vertexai_url_context_google",
      name: "URL Context",
      description: "Retrieve and analyze content from URLs provided in the prompt.",
      provider: "vertexai",
      type: "url_context",
      config: { url_context: {} } satisfies GoogleUrlContextToolParam,
    },
    {
      id: "vertexai_web_search_anthropic",
      name: "Web Search",
      description:
        "Search the web for real-time information. Claude uses this when current information would help.",
      provider: "vertexai",
      type: "web_search",
      config: {
        type: "web_search_20250305",
        name: "web_search",
      } satisfies AnthropicWebSearchToolParam,
    },
    {
      id: "vertexai_bash_anthropic",
      name: "Bash",
      description: "Execute bash commands.",
      provider: "vertexai",
      type: "bash",
      config: {
        type: "bash_20250124",
        name: "bash",
      } satisfies AnthropicBashToolParam,
    },
    {
      id: "vertexai_text_editor_anthropic",
      name: "Text Editor",
      description:
        "View, create, and edit text files with commands like view, str_replace, create, and insert.",
      provider: "vertexai",
      type: "text_editor",
      config: {
        type: "text_editor_20250728",
        name: "str_replace_based_edit_tool",
      } satisfies AnthropicTextEditorToolParam,
    },
  ],
  anthropic: [
    {
      id: "anthropic_web_search",
      name: "Web Search",
      description:
        "Search the web for real-time information. Claude uses this when current information would help.",
      provider: "anthropic",
      type: "web_search",
      config: {
        type: "web_search_20250305",
        name: "web_search",
      } satisfies AnthropicWebSearchToolParam,
    },
    {
      id: "anthropic_bash",
      name: "Bash",
      description: "Execute bash commands.",
      provider: "anthropic",
      type: "bash",
      config: {
        type: "bash_20250124",
        name: "bash",
      } satisfies AnthropicBashToolParam,
    },
    {
      id: "anthropic_code_execution",
      name: "Code Execution",
      description: "Execute code in a sandboxed environment with bash and a text editor.",
      provider: "anthropic",
      type: "code_interpreter",
      config: {
        type: "code_execution_20250825",
        name: "code_execution",
      } satisfies AnthropicCodeExecutionToolParam,
    },
    {
      id: "anthropic_text_editor",
      name: "Text Editor",
      description:
        "View, create, and edit text files with commands like view, str_replace, create, and insert.",
      provider: "anthropic",
      type: "text_editor",
      config: {
        type: "text_editor_20250728",
        name: "str_replace_based_edit_tool",
      } satisfies AnthropicTextEditorToolParam,
    },
  ],
};

/**
 * Map from resolved tool provider to a prefix used to filter Vertex built-in tool IDs.
 * Tools whose id starts with "vertexai_" and ends with "_<provider>" belong to that family.
 */
const VERTEX_TOOL_PROVIDER_TO_SUFFIX: Partial<Record<BuiltInToolProvider, string>> = {
  anthropic: "_anthropic",
  google: "_google",
};

export const getBuiltInToolsForProvider = (
  provider: string,
  options?: GetBuiltInToolsOptions,
): BuiltInTool[] => {
  const typedProvider = provider as BuiltInToolProvider;
  const tools = BUILT_IN_TOOLS[typedProvider] ?? [];

  if (
    (provider === "openai" || provider === "openai.azure") &&
    options?.apiType !== undefined &&
    options.apiType !== "responses"
  ) {
    return [];
  }

  if (provider === "vertexai") {
    const toolProvider = getVertexToolProvider(options?.modelName, options?.baseModel);
    const suffix = VERTEX_TOOL_PROVIDER_TO_SUFFIX[toolProvider];
    if (suffix) {
      return tools.filter(t => t.id.endsWith(suffix));
    }

    const defaultSuffix = VERTEX_TOOL_PROVIDER_TO_SUFFIX[VERTEX_DEFAULT_TOOL_PROVIDER] ?? "_google";
    return tools.filter(t => t.id.endsWith(defaultSuffix));
  }

  return tools;
};

/**
 * Built-in tool types supported for a given provider + api_type.
 *
 * Mirrors `getBuiltInToolsForProvider`, except that for OpenAI Chat Completions we
 * return an empty list — OpenAI server-side tools only ship through the Responses API.
 * For every other provider (Anthropic, Google, Vertex, OpenAI Responses), we return
 * the same set the "Add Tool" dropdown surfaces, so that
 * `removeUnsupportedBuiltInTools` doesn't strip tools the UI just offered.
 *
 * Why this matters: the previous implementation returned `[]` for every non-OpenAI-
 * Responses provider, which caused `removeUnsupportedBuiltInTools` (called from
 * `applyPromptBlueprintToState`) to wipe every Anthropic/Google/Vertex built-in tool
 * on each playground load — even tools that were persisted correctly on the server.
 */
export function getSupportedBuiltInToolTypes(
  provider?: string | null,
  apiType?: string | null,
  options?: GetBuiltInToolsOptions,
): BuiltInToolType[] {
  const p = provider?.toLowerCase() as BuiltInToolProvider | undefined;
  if (!p) {
    return [];
  }
  // OpenAI provider-native built-in tools require the Responses API.
  if ((p === "openai" || p === "openai.azure") && apiType !== "responses") {
    return [];
  }
  return getBuiltInToolsForProvider(p, { ...options, apiType }).map(t => t.type);
}

/**
 * Normalize provider names that share the same built-in tool set.
 * e.g. "openai.azure" → "openai" since they use identical tools.
 */
export const normalizeBuiltInToolProvider = (provider: string): string => {
  if (provider === "openai.azure") {
    return "openai";
  }
  return provider;
};

export const getAllBuiltInTools = (): BuiltInTool[] => Object.values(BUILT_IN_TOOLS).flat();

export const getBuiltInToolById = (id: string): BuiltInTool | undefined =>
  getAllBuiltInTools().find(tool => tool.id === id);
